import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { SketchPicker } from 'react-color';
import { usePropertyPanel } from '../PropertyPanelContext';
import RichEditorControl from '../CommonProperties/RichEditorControl';
import { SkinSelector } from '../CommonProperties';

const ImageTextProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // 컨텍스트에서 필요한 값들 가져오기
  const context = usePropertyPanel() || {};
  const {
    devicePropertyTab = 'pc',
    getDevicePropertyValue = (source, field, defaultValue) => defaultValue,
    handleImageUpload = async () => null,
    setIsRichEditorOpen,
    setRichEditorContent,
    renderPaddingControls
  } = context;
  
  // 컴포넌트 속성 직접 변경 함수
  const updateComponentProps = (property, value) => {
    if (!selectedComponent) return;
    
    // 디바이스별 속성 처리를 위한 깊은 복사
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    
    // 디바이스 속성 탭이 선택된 경우
    if (devicePropertyTab) {
      // 디바이스별 속성 객체 초기화
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = { pc: {}, mobile: {} };
        
        // 기존 속성을 PC/모바일에 복사
        Object.keys(existingProps).forEach(key => {
          if (key !== 'deviceProperty') {
            existingProps.deviceProperty.pc[key] = existingProps[key];
            existingProps.deviceProperty.mobile[key] = existingProps[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      
      existingProps.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingProps[property] = value;
      }
    } else {
      // 디바이스 속성 탭이 선택되지 않은 경우 모든 디바이스에 적용
      existingProps[property] = value;
      
      // 디바이스별 속성도 같이 업데이트
      if (existingProps.deviceProperty) {
        if (existingProps.deviceProperty.pc) {
          existingProps.deviceProperty.pc[property] = value;
        }
        if (existingProps.deviceProperty.mobile) {
          existingProps.deviceProperty.mobile[property] = value;
        }
      }
    }
    
    // Redux 상태 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  // 리치 에디터 저장 처리 함수
  const handleSaveContent = (content) => {
    if (!selectedComponent) return;
    
    // 디바이스 속성 탭이 선택된 경우 디바이스별로 저장
    if (devicePropertyTab) {
      const existingContent = selectedComponent.content ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        { deviceProperty: { pc: '', mobile: '' } };
      
      if (!existingContent.deviceProperty) {
        existingContent.deviceProperty = { pc: '', mobile: '' };
      }
      
      existingContent.deviceProperty[devicePropertyTab] = content;
      
      if (devicePropertyTab === 'pc') {
        existingContent.html = content;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: existingContent
        }
      }));
    } else {
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    }
  };
  
  // 현재 컨텐츠 가져오기
  const getCurrentContent = () => {
    if (!selectedComponent || !selectedComponent.content) return '';
    
    if (typeof selectedComponent.content === 'object') {
      if (selectedComponent.content.deviceProperty && 
          selectedComponent.content.deviceProperty[devicePropertyTab]) {
        return selectedComponent.content.deviceProperty[devicePropertyTab];
      } else if (selectedComponent.content.html) {
        return selectedComponent.content.html;
      }
    } else if (typeof selectedComponent.content === 'string') {
      return selectedComponent.content;
    }
    
    return '';
  };
  
  return (
    <>
      {/* 스킨 선택 UI */}
      <SkinSelector 
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => updateComponentProps('skin', skinId)}
      />
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-image"></i> 이미지-텍스트 설정
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>이미지 URL</label>
          <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              value={getDevicePropertyValue(selectedComponent.componentProps, 'imageUrl', 'https://via.placeholder.com/400x300')} 
              onChange={(e) => updateComponentProps('imageUrl', e.target.value)}
              style={{ flex: 1 }}
            />
            <label className="upload-button" style={{ 
              padding: '8px 12px', 
              backgroundColor: '#4a90e2', 
              color: 'white', 
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}>
              업로드
              <input 
                type="file" 
                style={{ display: 'none' }} 
                accept="image/*"
                onChange={async (e) => {
                  const imageUrl = await handleImageUpload(e);
                  if (imageUrl) {
                    updateComponentProps('imageUrl', imageUrl);
                  }
                }}
              />
            </label>
          </div>
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>이미지 위치</label>
          <select
            value={getDevicePropertyValue(selectedComponent.componentProps, 'imagePosition', 'left')}
            onChange={(e) => updateComponentProps('imagePosition', e.target.value)}
          >
            <option value="left">왼쪽</option>
            <option value="right">오른쪽</option>
          </select>
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>이미지 너비</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'imageWidth', '50%')}
            onChange={(e) => updateComponentProps('imageWidth', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>텍스트 너비</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'textWidth', '50%')}
            onChange={(e) => updateComponentProps('textWidth', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>간격</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'gap', '0%')}
            onChange={(e) => updateComponentProps('gap', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>세로 정렬</label>
          <select
            value={getDevicePropertyValue(selectedComponent.componentProps, 'verticalAlign', 'middle')}
            onChange={(e) => updateComponentProps('verticalAlign', e.target.value)}
          >
            <option value="top">상단</option>
            <option value="middle">중앙</option>
            <option value="bottom">하단</option>
          </select>
        </div>
      </div>
      
      {/* 두 번째 영역 설정: 텍스트 또는 이미지 */}
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-columns"></i> 두 번째 영역 설정
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>이미지 사용</label>
          <input 
            type="checkbox" 
            checked={getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false)} 
            onChange={(e) => updateComponentProps('useSecondImage', e.target.checked)}
          />
          <span style={{ marginLeft: '8px' }}>텍스트 대신 이미지 사용</span>
        </div>
        
        {getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false) && (
          <div className="property-group">
            <label style={{ minWidth: '30%' }}>두 번째 이미지 URL</label>
            <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
              <input 
                type="text" 
                value={getDevicePropertyValue(selectedComponent.componentProps, 'secondImageUrl', 'https://via.placeholder.com/400x300')} 
                onChange={(e) => updateComponentProps('secondImageUrl', e.target.value)}
                style={{ flex: 1 }}
              />
              <label className="upload-button" style={{ 
                padding: '8px 12px', 
                backgroundColor: '#4a90e2', 
                color: 'white', 
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
                업로드
                <input 
                  type="file" 
                  style={{ display: 'none' }} 
                  accept="image/*"
                  onChange={async (e) => {
                    try {
                      const imageUrl = await handleImageUpload(e);
                      
                      if (imageUrl) {
                        updateComponentProps('secondImageUrl', imageUrl);
                      }
                    } catch (error) {
                      console.error("두 번째 이미지 업로드 오류:", error);
                      alert("두 번째 이미지 업로드 중 오류가 발생했습니다.");
                    }
                  }}
                />
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* 텍스트 에디터 - 이미지 사용이 체크되지 않았을 때만 표시 */}
      {!getDevicePropertyValue(selectedComponent.componentProps, 'useSecondImage', false) && (
        <>
          {/* 기존 RichEditorControl */}
          <RichEditorControl 
            initialContent={getCurrentContent()}
            onContentSave={handleSaveContent}
            showPreview={true}
          />
        
        </>
      )}
      
      {/* 패딩 컨트롤 추가 */}
      {renderPaddingControls && renderPaddingControls()}
      
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-paint-brush"></i> 스타일 설정
        </div>
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>배경색</label>
          <SketchPicker 
            color={getDevicePropertyValue(selectedComponent.componentProps, 'backgroundColor', 'transparent')} 
            onChange={(color) => updateComponentProps('backgroundColor', color.hex)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>내부 여백</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'padding', '20px')}
            onChange={(e) => updateComponentProps('padding', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>테두리 둥글기</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'borderRadius', '0px')}
            onChange={(e) => updateComponentProps('borderRadius', e.target.value)}
          />
        </div>
      </div>
    </>
  );
};

export default ImageTextProperties;